import React, { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText,
  Plus,
  Pencil,
  Trash2,
  Download,
  Check,
  Loader2,
  ChevronDown,
  ChevronUp,
  X,
  AlertCircle,
  Search,
} from 'lucide-react';

const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0,00 €';
  return num.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const RemesaGenerator = () => {
  const { token, API_URL } = useAuth();

  // Step control
  const [step, setStep] = useState(1);

  // Date range
  const [fecha1, setFecha1] = useState('');
  const [fecha2, setFecha2] = useState('');

  // Data
  const [datos, setDatos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Expanded CIF groups
  const [expandedGroups, setExpandedGroups] = useState({});

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelectId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleIds = () => {
    const ids = [];
    datos.forEach((g) => (g.detalles || []).forEach((d) => ids.push(d.id)));
    return ids;
  };

  const toggleSelectAll = () => {
    const ids = allVisibleIds();
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(ids));
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`¿Seguro que desea eliminar ${selectedIds.size} facturas de la remesa?`)) return;
    try {
      clearMessages();
      const res = await fetch(`${API_URL}/contabilidad/remesas/datos/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al eliminar');
      }
      setSuccessMsg(`${selectedIds.size} factura(s) eliminada(s) correctamente`);
      setSelectedIds(new Set());
      await fetchDatos(fecha1, fecha2);
    } catch (err) {
      setError(err.message);
    }
  };

  // Add invoice modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ numFactura: '' });
  const [addLoading, setAddLoading] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ id: null, num_factura: '', importe: '', iban: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Action loading states
  const [creatingFile, setCreatingFile] = useState(false);
  const [markingEnviada, setMarkingEnviada] = useState(false);

  const clearMessages = () => {
    setError('');
    setSuccessMsg('');
  };

  const fetchDatos = useCallback(async (f1, f2) => {
    try {
      setLoading(true);
      clearMessages();
      const params = new URLSearchParams({ fecha1: f1, fecha2: f2 });
      const res = await fetch(`${API_URL}/contabilidad/remesas/datos?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al cargar datos de remesa');
      }
      const data = await res.json();
      setDatos(data.datos || []);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, API_URL]);

  const handleGenerar = async () => {
    if (!fecha1 || !fecha2) {
      setError('Debe seleccionar ambas fechas');
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      const res = await fetch(`${API_URL}/contabilidad/remesas/generar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fecha1, fecha2 }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al generar remesa');
      }
      const data = await res.json();
      setSuccessMsg(data.message || `Remesa generada: ${data.count} registros`);
      await fetchDatos(fecha1, fecha2);
      setStep(2);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAddInvoice = async () => {
    if (!addForm.numFactura.trim()) {
      setError('Debe ingresar un número de factura');
      return;
    }
    try {
      setAddLoading(true);
      clearMessages();
      const res = await fetch(`${API_URL}/contabilidad/remesas/datos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ numFactura: addForm.numFactura, fecha1, fecha2 }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al agregar factura');
      }
      setShowAddModal(false);
      setAddForm({ numFactura: '' });
      setSuccessMsg('Factura agregada correctamente');
      // Re-generate remesa to include the newly added invoice in remesas_datos
      await fetch(`${API_URL}/contabilidad/remesas/generar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fecha1, fecha2 }),
      });
      await fetchDatos(fecha1, fecha2);
    } catch (err) {
      setError(err.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = (detalle) => {
    setEditForm({
      id: detalle.id,
      num_factura: detalle.num_factura || '',
      importe: detalle.importe || '',
      iban: detalle.iban || '',
    });
    setShowEditModal(true);
    clearMessages();
  };

  const handleSaveEdit = async () => {
    try {
      setEditLoading(true);
      clearMessages();
      const res = await fetch(`${API_URL}/contabilidad/remesas/datos/${editForm.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          num_factura: editForm.num_factura,
          // Importe tolerante a coma decimal ES ("141,81") sin perder decimales.
          // parseFloat("141,81") daba 141; el back tambien lo re-parsea por si acaso.
          importe: (() => {
            let s = String(editForm.importe ?? '').trim();
            if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
            const n = parseFloat(s);
            return isNaN(n) ? 0 : n;
          })(),
          iban: editForm.iban,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al actualizar');
      }
      setShowEditModal(false);
      setSuccessMsg('Registro actualizado correctamente');
      await fetchDatos(fecha1, fecha2);
    } catch (err) {
      setError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Seguro que desea eliminar este registro de la remesa?')) return;
    try {
      clearMessages();
      const res = await fetch(`${API_URL}/contabilidad/remesas/datos/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al eliminar');
      }
      setSuccessMsg('Registro eliminado correctamente');
      await fetchDatos(fecha1, fecha2);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCrearArchivo = async () => {
    try {
      setCreatingFile(true);
      clearMessages();
      const res = await fetch(`${API_URL}/contabilidad/remesas/generar-archivo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fecha1, fecha2 }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al generar archivo');
      }
      // Extract filename from Content-Disposition header (matches PHP format: HPF-fecha1-fecha2-total.txt)
      const disposition = res.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
      const downloadName = filenameMatch ? filenameMatch[1] : `HPF-${fecha1}-${fecha2}.txt`;

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccessMsg('Archivo descargado correctamente');
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingFile(false);
    }
  };

  const handleMarcarEnviada = async () => {
    if (!window.confirm('¿Seguro que desea marcar esta remesa como enviada al banco?')) return;
    try {
      setMarkingEnviada(true);
      clearMessages();
      const res = await fetch(`${API_URL}/contabilidad/remesas/marcar-insertado`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fecha1, fecha2 }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al marcar como enviada');
      }
      alert('Remesa marcada como enviada al banco correctamente');
      setStep(1);
      setDatos([]);
      setTotal(0);
      setExpandedGroups({});
    } catch (err) {
      setError(err.message);
    } finally {
      setMarkingEnviada(false);
    }
  };

  const toggleGroup = (cif) => {
    setExpandedGroups((prev) => ({ ...prev, [cif]: !prev[cif] }));
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setDatos([]);
    setTotal(0);
    setExpandedGroups({});
    clearMessages();
  };

  const totalFacturas = datos.reduce((sum, g) => sum + (g.detalles ? g.detalles.length : 0), 0);
  const totalImporte = datos.reduce((sum, g) => sum + parseFloat(g.importe || 0), 0);

  // ──────── STEP 1: Date Selection ────────
  if (step === 1) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Generador de Remesas
          </h1>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
              <button onClick={() => setError('')} className="ml-auto">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {successMsg && (
            <div className="mb-4 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              <Check className="w-4 h-4 flex-shrink-0" />
              <span>{successMsg}</span>
              <button onClick={() => setSuccessMsg('')} className="ml-auto">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="max-w-lg mx-auto mt-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Seleccionar Rango de Fechas</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Desde</label>
                  <input
                    type="date"
                    value={fecha1}
                    onChange={(e) => setFecha1(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Hasta</label>
                  <input
                    type="date"
                    value={fecha2}
                    onChange={(e) => setFecha2(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <button
                  onClick={handleGenerar}
                  disabled={loading || !fecha1 || !fecha2}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" />
                      Generar Remesa
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ──────── STEP 2: Remesa Verification ────────
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              Verificador de Remesas
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Rango: <span className="font-medium">{formatDate(fecha1)}</span> — <span className="font-medium">{formatDate(fecha2)}</span>
              <button
                onClick={handleBackToStep1}
                className="ml-3 text-blue-600 hover:text-blue-800 underline text-sm"
              >
                Cambiar Fechas
              </button>
            </p>
          </div>
          <button
            onClick={() => { setShowAddModal(true); clearMessages(); }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Agregar Factura
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-shrink-0 px-6">
        {error && (
          <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {successMsg && (
          <div className="mt-3 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
            <Check className="w-4 h-4 flex-shrink-0" />
            <span>{successMsg}</span>
            <button onClick={() => setSuccessMsg('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex-shrink-0 px-6 pt-3">
          <div className="sticky top-0 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between z-20">
            <span className="text-sm text-red-800 font-medium">
              {selectedIds.size} factura(s) seleccionada(s)
            </span>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md flex items-center gap-2"
            >
              <Trash2 size={14} /> Eliminar seleccionadas
            </button>
          </div>
        </div>
      )}

      {/* Table Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-3 text-gray-500">Cargando datos...</span>
          </div>
        ) : datos.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No se encontraron datos para este rango de fechas</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase w-10">
                    <input
                      type="checkbox"
                      checked={(() => { const ids = allVisibleIds(); return ids.length > 0 && ids.every((id) => selectedIds.has(id)); })()}
                      onChange={toggleSelectAll}
                      style={{ accentColor: '#0099FF' }}
                      className="cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CIF/NIF</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">R. Social</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Importe Total</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Nº Facturas</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Detalles</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {datos.map((grupo, idx) => {
                  const isNegative = parseFloat(grupo.importe) < 0;
                  const isExpanded = expandedGroups[grupo.cif];
                  const hasMultiple = grupo.detalles && grupo.detalles.length > 1;
                  const showInline = grupo.detalles && grupo.detalles.length === 1;

                  return (
                    <React.Fragment key={grupo.cif}>
                      {/* Main group row */}
                      <tr className={`${isNegative ? 'bg-red-50' : 'bg-blue-50/70'} hover:bg-blue-100/70 transition-colors`}>
                        <td className="px-3 py-3 text-center">
                          {(() => {
                            const ids = (grupo.detalles || []).map((d) => d.id);
                            const allSel = ids.length > 0 && ids.every((id) => selectedIds.has(id));
                            return (
                              <input
                                type="checkbox"
                                checked={allSel}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setSelectedIds((prev) => {
                                    const next = new Set(prev);
                                    if (allSel) ids.forEach((id) => next.delete(id));
                                    else ids.forEach((id) => next.add(id));
                                    return next;
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{ accentColor: '#0099FF' }}
                                className="cursor-pointer"
                              />
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{idx + 1}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-700">{grupo.cif}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{grupo.razon_social}</td>
                        <td className={`px-4 py-3 text-sm text-right font-semibold ${isNegative ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(grupo.importe)}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-600">
                          {grupo.detalles ? grupo.detalles.length : 0}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {hasMultiple ? (
                            <button
                              onClick={() => toggleGroup(grupo.cif)}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              Detalles
                            </button>
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </td>
                      </tr>

                      {/* Inline single invoice detail */}
                      {showInline && (
                        <tr className="bg-gray-50/50 border-b border-gray-200">
                          <td colSpan={7} className="px-4 py-2">
                            <div className="ml-8">
                              <DetailRow
                                detalle={grupo.detalles[0]}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                selected={selectedIds.has(grupo.detalles[0].id)}
                                onToggleSelect={toggleSelectId}
                              />
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Expanded sub-table for multiple invoices */}
                      {hasMultiple && isExpanded && (
                        <tr className="border-b border-gray-200">
                          <td colSpan={7} className="p-0">
                            <div className="bg-gray-50/50 px-8 py-3">
                              <table className="min-w-full">
                                <thead>
                                  <tr className="border-b border-gray-200">
                                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-10"></th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Factura</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Importe</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nº Cuenta (IBAN)</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha Cobro</th>
                                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Editar</th>
                                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Borrar</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {grupo.detalles.map((det) => (
                                    <DetailRow
                                      key={det.id}
                                      detalle={det}
                                      onEdit={handleEdit}
                                      onDelete={handleDelete}
                                      selected={selectedIds.has(det.id)}
                                      onToggleSelect={toggleSelectId}
                                      asTableRow
                                    />
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-800">
            Total Facturas: {totalFacturas} — {formatCurrency(totalImporte)}
          </p>
        </div>
        <div className="flex items-center justify-center gap-3 mt-3">
          <button
            onClick={handleCrearArchivo}
            disabled={creatingFile || datos.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {creatingFile ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Crear Archivo
              </>
            )}
          </button>
          <button
            onClick={handleMarcarEnviada}
            disabled={markingEnviada || datos.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {markingEnviada ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Marcar como Enviada
              </>
            )}
          </button>
        </div>
      </div>

      {/* Add Invoice Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Agregar Factura</h3>
              <button
                onClick={() => { setShowAddModal(false); setAddForm({ numFactura: '' }); }}
              >
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número de Factura</label>
                <input
                  type="text"
                  value={addForm.numFactura}
                  onChange={(e) => setAddForm({ numFactura: e.target.value })}
                  placeholder="Ej: FAC-2024-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => { setShowAddModal(false); setAddForm({ numFactura: '' }); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddInvoice}
                disabled={addLoading || !addForm.numFactura.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {addLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Agregando...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Agregar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Editar Registro</h3>
              <button onClick={() => setShowEditModal(false)}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número de Factura</label>
                <input
                  type="text"
                  value={editForm.num_factura}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, num_factura: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Importe</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.importe}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, importe: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
                <input
                  type="text"
                  value={editForm.iban}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, iban: e.target.value }))}
                  placeholder="ES00 0000 0000 0000 0000 0000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={editLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {editLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Guardar Cambios
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ──────── Detail Row Component ────────
const DetailRow = ({ detalle, onEdit, onDelete, selected = false, onToggleSelect, asTableRow = false }) => {
  const isNegative = parseFloat(detalle.importe) < 0;
  const emptyIban = !detalle.iban || detalle.iban.trim() === '';

  const content = (
    <>
      <td className="px-3 py-2 text-center w-10">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => {
            e.stopPropagation();
            if (onToggleSelect) onToggleSelect(detalle.id);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{ accentColor: '#0099FF' }}
          className="cursor-pointer"
        />
      </td>
      <td className="px-3 py-2 text-sm text-gray-900">{detalle.num_factura || '-'}</td>
      <td className={`px-3 py-2 text-sm text-right font-medium ${isNegative ? 'text-red-600' : 'text-gray-900'}`}>
        {formatCurrency(detalle.importe)}
      </td>
      <td className={`px-3 py-2 text-sm font-mono ${emptyIban ? 'text-amber-600' : 'text-gray-700'}`}>
        {detalle.iban || <span className="italic text-amber-500">Sin IBAN</span>}
      </td>
      <td className="px-3 py-2 text-sm text-gray-600">{formatDate(detalle.fecha_cobro)}</td>
      <td className="px-3 py-2 text-center">
        <button
          onClick={() => onEdit(detalle)}
          className="p-1.5 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
          title="Editar"
        >
          <Pencil className="w-4 h-4" />
        </button>
      </td>
      <td className="px-3 py-2 text-center">
        <button
          onClick={() => onDelete(detalle.id)}
          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
          title="Eliminar"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </>
  );

  if (asTableRow) {
    return (
      <tr className={`${isNegative ? 'bg-red-50/50' : ''} ${emptyIban ? 'bg-yellow-50/50' : ''} hover:bg-blue-50/50 transition-colors`}>
        {content}
      </tr>
    );
  }

  // Inline single-row display
  return (
    <table className="min-w-full">
      <tbody>
        <tr className={`${isNegative ? 'bg-red-50/50' : ''} ${emptyIban ? 'bg-yellow-50/50' : ''}`}>
          {content}
        </tr>
      </tbody>
    </table>
  );
};

export default RemesaGenerator;
